import type { ReactNode } from 'react';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import { Avatar, type AvatarSize } from '../../design-system/primitives';
import type { CardSlot } from '../assembly';
import { fieldLabel, type ObjectSummary } from '../format';
import { columnFor, type CellRenderer, type Column } from './cells';
import type { Row } from './useCollection';

/**
 * Carte en zones JSON (ADR-100), la même définition pour `CardList`, `Kanban`, l'Agenda et leurs
 * infobulles. Chaque zone lit des **champs** de l'enregistrement, formatés par le modèle de l'objet :
 * libellé d'énumération, référence par son libellé (lu sous l'autorisation de l'utilisateur),
 * montant exact, date civile, « Masqué ». Le cadre (sélection, ouverture, glisser) est au conteneur.
 */

export type CardZones = Extract<CardSlot, { zones: unknown }>;
export type CardZone = CardZones['zones'][number];
type ZoneOf<T extends CardZone['type']> = Extract<CardZone, { type: T }>;

const IDENTITY_AVATAR: Readonly<Record<NonNullable<ZoneOf<'identity'>['size']>, AvatarSize>> = { sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' };

/** Valeur numérique d'un champ (nombre, décimal en chaîne, mesure { value }) pour une barre : présentation, jamais un montant (R2). */
function ratioOf(raw: unknown): number | null {
  const value = typeof raw === 'object' && raw !== null && 'value' in raw ? (raw as { value: unknown }).value : raw;
  const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(number) ? number : null;
}

export interface CardZonesProps {
  zones: readonly CardZone[];
  row: Row;
  object: ObjectSummary | undefined;
  columns: readonly Column[];
  render: CellRenderer;
  /** Titre de la zone identité : rendu par l'hôte (bouton d'ouverture), sinon texte. */
  renderTitle?: (title: string) => ReactNode;
}

export function CardZonesView({ zones, row, object, columns, render, renderTitle }: CardZonesProps) {
  const label = (fieldId: string, own?: Readonly<Record<string, string>>): string => (own === undefined ? fieldLabel(object, fieldId, columnFor(fieldId, columns).label) : text(own));
  const empty = (value: string): boolean => value === '' || value === '—';
  const badge = (fieldId: string, tones?: Readonly<Record<string, string>>): ReactNode => {
    const column = columnFor(fieldId, columns);
    const toned: Column = { ...column, tones: (tones ?? column.tones ?? {}) as NonNullable<Column['tones']> };
    return <span key={fieldId}>{render.cell(toned, row)}</span>;
  };
  return (
    <div className="ay-zones">
      {zones.map((zone, index) => {
        switch (zone.type) {
          case 'identity': {
            const title = render.display(zone.title_field, row);
            const subtitle = zone.subtitle_field === undefined ? undefined : render.display(zone.subtitle_field, row);
            // Image (ADR-103) : le champ fichier déclaré par la zone, servi par URL signée ; initiales du titre à défaut.
            const src = zone.image_field === undefined ? undefined : render.image(zone.image_field, row);
            return (
              <div key={index} className={`ay-zone-identity ay-zone-identity--${zone.size ?? 'md'}`}>
                <Avatar label={title} src={src} size={IDENTITY_AVATAR[zone.size ?? 'md']} shape={zone.shape ?? 'rounded'} {...(zone.icon === undefined || src !== undefined ? {} : { icon: zone.icon })} />
                <div className="ay-zone-identity__text">
                  <span className="ay-zone-identity__title">{renderTitle === undefined ? title : renderTitle(title)}</span>
                  {subtitle !== undefined && !empty(subtitle) && <span className="ay-zone-identity__subtitle">{subtitle}</span>}
                  {(zone.pill_field !== undefined || zone.badge_field !== undefined) && (
                    <span className="ay-zone-identity__chips">
                      {zone.pill_field !== undefined && !empty(render.display(zone.pill_field, row)) && <span className="ay-chip">{render.display(zone.pill_field, row)}</span>}
                      {zone.badge_field !== undefined && badge(zone.badge_field)}
                    </span>
                  )}
                </div>
              </div>
            );
          }
          case 'line': {
            const value = render.display(zone.field, row);
            return (
              <div key={index} className={`ay-zone-line ay-zone-line--${empty(value) ? 'muted' : (zone.tone ?? 'default')}${zone.mono === true ? ' ay-zone--mono' : ''}`} title={label(zone.field, zone.label)}>
                {zone.icon !== undefined && <Icon name={zone.icon} size={14} />}
                <span className="ay-zone-line__value">{empty(value) ? '—' : render.cell(columnFor(zone.field, columns), row)}</span>
              </div>
            );
          }
          case 'row':
            return (
              <div key={index} className="ay-zone-row">
                {zone.cells.map((cell) => (
                  <div key={cell.field} className={`ay-zone-row__cell${cell.align === 'end' ? ' ay-zone-row__cell--end' : ''}`} title={label(cell.field, cell.label)}>
                    {cell.label !== undefined && <span className="ay-zone-row__label">{text(cell.label)}</span>}
                    <span className={`ay-zone-row__value${cell.mono === true ? ' ay-zone--mono' : ''}`}>
                      {cell.icon !== undefined && <Icon name={cell.icon} size={13} />}
                      {render.cell(columnFor(cell.field, columns), row)}
                    </span>
                  </div>
                ))}
              </div>
            );
          case 'keyvalue':
            return (
              <dl key={index} className="ay-zone-kv">
                {zone.fields.map((fieldId) => (
                  <div key={fieldId} className="ay-zone-kv__item">
                    <dt>{label(fieldId)} :</dt>
                    <dd>{render.cell(columnFor(fieldId, columns), row)}</dd>
                  </div>
                ))}
              </dl>
            );
          case 'badges':
            return (
              <div key={index} className="ay-zone-badges">
                {zone.fields.map((fieldId) => badge(fieldId, zone.tones))}
              </div>
            );
          case 'progress': {
            const raw = ratioOf(row[zone.field]);
            const max = zone.max ?? 100;
            const percent = raw === null ? null : Math.max(0, Math.min(100, (raw / max) * 100));
            const tone = raw === null ? 'muted' : zone.danger_at !== undefined && raw >= zone.danger_at ? 'danger' : zone.warn_at !== undefined && raw >= zone.warn_at ? 'warn' : 'ok';
            return (
              <div key={index} className="ay-zone-progress">
                <div className="ay-zone-progress__head">
                  <span>{label(zone.field, zone.label)}</span>
                  <span className="ay-zone--mono">{raw === null ? '—' : `${render.display(zone.field, row)}${zone.unit === undefined ? '' : ` ${zone.unit}`}`}</span>
                </div>
                <div className={`ay-progress ay-progress--${tone}`} role="progressbar" aria-label={label(zone.field, zone.label)} aria-valuemin={0} aria-valuemax={max} {...(raw === null ? {} : { 'aria-valuenow': raw })}>
                  <span style={{ width: `${percent ?? 0}%` }} />
                </div>
              </div>
            );
          }
          case 'metric':
            return (
              <div key={index} className="ay-zone-metric">
                <span className="ay-zone-metric__value">{render.cell(columnFor(zone.field, columns), row)}</span>
                {zone.unit !== undefined && <span className="ay-zone-metric__unit">{zone.unit}</span>}
                <span className="ay-zone-metric__label">{label(zone.field, zone.label)}</span>
              </div>
            );
          case 'paragraph':
            return (
              <p key={index} className="ay-zone-paragraph" style={{ WebkitLineClamp: zone.lines ?? 3 }}>
                {render.display(zone.field, row)}
              </p>
            );
          case 'person': {
            const name = render.display(zone.field, row);
            return (
              <div key={index} className="ay-zone-person">
                {!empty(name) && <Avatar label={name} src={render.image(zone.field, row)} size="sm" shape="circle" />}
                <span className="ay-zone-person__text">
                  {zone.label !== undefined && <span className="ay-zone-person__label">{text(zone.label)}</span>}
                  <span className="ay-zone-person__name">{empty(name) ? '—' : name}</span>
                </span>
              </div>
            );
          }
          case 'divider':
            return <hr key={index} className="ay-zone-divider" />;
          default:
            return null;
        }
      })}
    </div>
  );
}

/** Champs que des zones affichent (lecture des libellés de référence par type, ADR-094). */
export function zoneFields(zones: readonly CardZone[] | undefined): string[] {
  const out: string[] = [];
  for (const zone of zones ?? []) {
    const record = zone as unknown as Record<string, unknown>;
    for (const key of ['title_field', 'subtitle_field', 'image_field', 'pill_field', 'badge_field', 'field']) if (typeof record[key] === 'string') out.push(record[key] as string);
    if (Array.isArray(record['fields'])) out.push(...(record['fields'] as string[]));
    if (Array.isArray(record['cells'])) out.push(...(record['cells'] as Array<{ field: string }>).map((cell) => cell.field));
  }
  return [...new Set(out)];
}

/** Zones par défaut d'une carte sans modèle : identité sur la première colonne, les suivantes en « libellé : valeur ». */
export function defaultZones(columns: readonly Column[]): CardZone[] {
  const [first, ...rest] = columns.map((column) => column.field);
  const zones: CardZone[] = [{ type: 'identity', title_field: first ?? 'id' }];
  if (rest.length > 0) zones.push({ type: 'keyvalue', fields: rest.slice(0, 5) } as unknown as CardZone);
  return zones;
}

